import React, { useEffect, useState, useRef, useCallback } from 'react';

export interface EncodingProgressOverlayProps {
  visible: boolean;
  fileId: number | undefined;
  downloadUrl?: string;
  title?: string;
  fileTypeName?: string;
  fileSize?: number;
  fileIconUrl?: string;
  /** When set, a "Switch to Proxy" button is shown so the user can fall back
   *  to a browser-playable proxy instead of the unplayable original. */
  onSwitchToProxy?: () => void;
}

interface EncodingStatus {
  status: 'queued' | 'processing' | 'done';
  position?: number;
  progress?: number;
}

/** Poll interval in ms. */
const POLL_INTERVAL = 5000;

/**
 * Overlay shown when a file's URL is reachable but not browser-playable.
 * Polls /encoding/progress?id={fileId} to show queue position or encoding
 * progress. Once done, reloads the page. Clickable to download the original.
 */
export const EncodingProgressOverlay = React.memo(function EncodingProgressOverlay({
  visible,
  fileId,
  downloadUrl,
  title,
  fileTypeName,
  fileSize,
  fileIconUrl,
  onSwitchToProxy,
}: EncodingProgressOverlayProps) {
  const [status, setStatus] = useState<EncodingStatus | null>(null);
  const [error, setError] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const mountedRef = useRef(true);
  const progressBarRef = useRef<HTMLElement | null>(null);

  const poll = useCallback(async () => {
    if (!fileId) return;
    try {
      const resp = await fetch(`/encoding/progress?id=${fileId}`);
      if (!resp.ok) {
        setError(true);
        return;
      }
      const data: EncodingStatus = await resp.json();
      if (!mountedRef.current) return;

      if (data.status === 'done') {
        // Proxy is ready — reload to pick up the new source
        window.location.reload();
        return;
      }

      setStatus(data);
      setError(false);
    } catch {
      if (mountedRef.current) setError(true);
    }
  }, [fileId]);

  useEffect(() => {
    mountedRef.current = true;
    if (!visible || !fileId) return;

    // Poll immediately, then on interval
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [visible, fileId, poll]);

  // Imperatively update the progress-bar-circular custom element attribute
  // because React doesn't reliably sync attributes on custom elements.
  const progressValue = status?.status === 'processing' ? (status.progress ?? 0) : 0;
  useEffect(() => {
    if (progressBarRef.current) {
      progressBarRef.current.setAttribute('progress', String(progressValue));
    }
  }, [progressValue]);

  if (!visible) return null;

  const isQueued = status?.status === 'queued';
  const isProcessing = status?.status === 'processing';

  let statusText: string;
  if (error || !status) {
    statusText = 'This file is being prepared for playback.';
  } else if (isQueued) {
    statusText = status.position === 1
      ? 'This file is next in the queue.'
      : `This file is in position ${status.position} in the queue.`;
  } else if (isProcessing) {
    statusText = 'This file is being optimized for playback.';
  } else {
    statusText = 'Preparing file for playback...';
  }

  const iconSrc = fileIconUrl || '/img/Unknown_Doc.svg';

  const content = (
    <div className="kollab-player-encoding-overlay">
      <div className="kollab-player-encoding-box">
        <div className="kollab-player-encoding-header">
          {title && <h2 className="kollab-player-encoding-title">{title}</h2>}
          {!title && <h2 className="kollab-player-encoding-title">Optimizing for playback</h2>}
        </div>

        <div className="kollab-player-encoding-progress-area">
          {React.createElement('progress-bar-circular' as any, {
            ref: (el: HTMLElement | null) => { progressBarRef.current = el; },
            width: '80',
            height: '80',
            'image-url': iconSrc,
            progress: String(progressValue),
          })}
        </div>

        <div className="kollab-player-encoding-status">
          <p className="kollab-player-encoding-status-text">{statusText}</p>
          {isProcessing && (
            <p className="kollab-player-encoding-percent">{progressValue}%</p>
          )}
          {fileTypeName && (
            <p className="kollab-player-encoding-meta">
              {fileTypeName}
              {fileSize != null && fileSize > 0 && (
                <> &middot; {formatSize(fileSize)}</>
              )}
            </p>
          )}
        </div>

        {onSwitchToProxy && (
          <button
            className="kollab-player-switch-proxy-btn"
            onClick={(e) => { e.preventDefault(); onSwitchToProxy(); }}
            type="button"
          >
            Switch to Proxy
          </button>
        )}
        {downloadUrl && (
          <div className="kollab-player-encoding-download">
            Click to download the original file
          </div>
        )}
      </div>
    </div>
  );

  if (downloadUrl) {
    return (
      <div className="kollab-player-download-overlay kollab-player-download-overlay--visible">
        <a href={downloadUrl} className="kollab-player-encoding-link">
          {content}
        </a>
      </div>
    );
  }

  return (
    <div className="kollab-player-download-overlay kollab-player-download-overlay--visible">
      {content}
    </div>
  );
});

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
